import {
  Component,
  ElementRef,
  Input,
  Output,
  EventEmitter,
  ViewChild,
  ChangeDetectorRef,
} from '@angular/core';
import { Chart, ChartType, registerables } from 'chart.js';
import { PTicketService } from '../../../services/p-ticket.service';
import { report } from '../../../interfaces/preport';
import { FormsModule } from '@angular/forms';
import { NgIf } from '@angular/common';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';

Chart.register(...registerables);

interface dataset {
  label: string[];
  data: number[];
}

@Component({
  selector: 'app-reporting-graph',
  standalone: true,
  imports: [
    FormsModule,
    NgIf,
    MatProgressSpinnerModule,
    MatButtonModule,
    MatIconModule,
  ],
  templateUrl: './reporting-graph.component.html',
  styleUrls: ['./reporting-graph.component.scss'],
})
export class ReportingGraphComponent {
  @Input() report!: report;

  /** Emitted when the user clicks a chart segment; carries the field to filter
   * by and the value to filter for. Value may be a plain value for an exact
   * match, or a "$startsWith:" / "$in:" prefixed string understood by the
   * backend's buildFilterWhere. */
  @Output() segmentClicked = new EventEmitter<{ field: string; value: string }>();

  @ViewChild('chartCanvas', { static: true })
  chartCanvas!: ElementRef<HTMLCanvasElement>;

  chart!: Chart;
  isLoading: boolean = false;

  chartType: ChartType = 'bar';
  dataset: dataset = { label: [], data: [] };

  /** Raw (un-normalized, un-suffixed) bin values per group, index-aligned with
   * dataset.data / dataset.label. A group can have multiple raw values if
   * several distinct raw values were normalized into the same display bucket
   * (e.g. several "default_*" variants collapsed into "unknown"). */
  rawBinKeys: string[][] = [];

  readonly defaultColors = [
    '#1f77b4',
    '#ff7f0e',
    '#2ca02c',
    '#d62728',
    '#9467bd',
    '#8c564b',
    '#e377c2',
    '#7f7f7f',
    '#bcbd22',
    '#17becf',
    '#393b79',
    '#637939',
    '#8c6d31',
    '#843c39',
    '#7b4173',
    '#5254a3',
    '#9c9ede',
    '#cedb9c',
    '#f7b6d2',
    '#c7c7c7',
  ];

  data = {
    labels: this.dataset.label,
    datasets: [
      {
        label: 'Result',
        data: this.dataset.data,
        backgroundColor: this.defaultColors,
      },
    ],
  };

  constructor(
    private pTicketService: PTicketService,
    private cdRef: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    if (this.report) {
      this.loadTickets(this.report);
    }
  }

  graphUpdate(event: Event): void {}

  loadTickets(report: report): void {
    this.isLoading = true;
    this.cdRef.detectChanges();
    if (this.chart) this.chart.destroy();

    // NOTE: 'pkeywords' is a hasMany join table, not a scalar pTicket column,
    // so the backend's findForReport (GROUP BY on whitelisted columns) can't
    // bin by it. If rep_xAxisOption.key === 'pkeywords' this call will 400.
    this.pTicketService
      .getForReport(report.rep_filter, report.rep_xAxisOption.key)
      .subscribe((bins) => {
        this.chartType = report.rep_chartType;

        // Backend already grouped/counted; just normalize 'default_*' -> 'unknown'
        // and merge any duplicate bins that collapse onto the same label.
        const grouped = new Map<string, number>();
        const rawValuesForGroup = new Map<string, string[]>();

        for (const { bin, count } of bins) {
          let k = String(bin ?? 'Unknown');
          const originalRaw = k;
          if (k.includes('default_')) {
            k = 'unknown';
          }
          grouped.set(k, (grouped.get(k) || 0) + Number(count));

          if (!rawValuesForGroup.has(k)) {
            rawValuesForGroup.set(k, []);
          }
          rawValuesForGroup.get(k)!.push(originalRaw);
        }

        const groupKeys = Array.from(grouped.keys());

        this.dataset = {
          label: groupKeys.map((key) => `${key} (${grouped.get(key)})`),
          data: groupKeys.map((key) => grouped.get(key)!),
        };

        // index-aligned raw values, used to build the drill-down filter on click
        this.rawBinKeys = groupKeys.map((key) => rawValuesForGroup.get(key)!);

        this.data = {
          labels: this.dataset.label,
          datasets: [
            {
              ...this.data.datasets[0],
              data: this.dataset.data,
            },
          ],
        };

        this.isLoading = false;
        this.cdRef.detectChanges();

        if (this.dataset.data.length !== 0) {
          this.createChart();
        }
      });
  }

  createChart(): void {
    if (!this.chartCanvas) return;
    if (this.chart) this.chart.destroy();

    const commonOptions: any = {
      responsive: true,
      maintainAspectRatio: false,
      onHover: (event: any, elements: any[]) => {
        const target = event.native?.target as HTMLElement;
        if (target) {
          target.style.cursor = elements.length ? 'pointer' : 'default';
        }
      },
      onClick: (_event: any, elements: any[]) => {
        if (!elements.length) return;
        const idx = elements[0].index;
        const values = this.rawBinKeys[idx];
        if (!values || values.length === 0) return;

        // If this bucket aggregated multiple raw "default_*" variants (or is
        // otherwise made up of more than one raw value), filter by prefix
        // instead of a single exact value so the drill-down matches exactly
        // what the chart bucket represents.
        const isAggregatedUnknown =
          values.length > 1 || values[0]?.includes('default_');
        const filterValue = isAggregatedUnknown
          ? '$startsWith:default_'
          : values[0];

        this.segmentClicked.emit({
          field: this.report.rep_xAxisOption.key as string,
          value: filterValue,
        });
      },
    };

    // big charts | doughnut, pie and radar
    if (
      this.chartType == 'pie' ||
      this.chartType == 'doughnut' ||
      this.chartType == 'radar'
    ) {
      this.chart = new Chart(this.chartCanvas.nativeElement, {
        type: this.chartType,
        data: this.data,

        options: {
          ...commonOptions,
          scales: {},
        },
      });
    } else {
      this.chart = new Chart(this.chartCanvas.nativeElement, {
        type: this.chartType,
        data: this.data,

        options: {
          ...commonOptions,
          scales: {
            y: {
              beginAtZero: true,
            },
            x: {
              beginAtZero: true,
              ticks: {
                autoSkip: false,
              },
            },
          },
        },
      });
    }
  }

  onTypeChange(newType: ChartType) {
    this.chartType = newType;
    this.createChart();
  }
}